import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, Send, Users, Car, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../context/LanguageContext';
import SearchableLocation from './SearchableLocation';

const VehicleBookingDialog = ({ vehicle, open, onOpenChange }) => {
  const { lang } = useLanguage();
  const { settings } = useSettings();
  const [airports, setAirports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [tripType, setTripType] = useState('one-way');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', whatsapp: '',
    pickup: '', destination: '',
    date: '', time: '',
    passengers: '2', comments: '',
  });

  useEffect(() => {
    if (open) {
      api.get('/airports').then(r => setAirports(r.data)).catch(()=>{});
      api.get('/hotels').then(r => setHotels(r.data)).catch(()=>{});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !form.pickup || !form.destination) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    const t = setTimeout(() => {
      api.post('/quote', {
        pickup: form.pickup,
        destination: form.destination,
        passengers: parseInt(form.passengers || '2', 10),
        trip_type: tripType,
      }).then(r => {
        setQuote(r.data);
        setQuoteLoading(false);
      }).catch(() => setQuoteLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [open, form.pickup, form.destination, form.passengers, tripType]);

  if (!vehicle) return null;

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const vName = lang === 'en' && vehicle.name_en ? vehicle.name_en : vehicle.name;
  // Find the price for this specific vehicle in the quote
  const myQuote = quote?.ok ? quote.quotes.find(q => q.vehicle_key === vehicle.pricing_key) : null;
  const estimatedPrice = myQuote?.price_total;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.whatsapp || !form.pickup || !form.destination || !form.date) {
      toast.error(lang === 'es' ? 'Completa los campos requeridos' : 'Please complete required fields');
      return;
    }
    setLoading(true);
    try {
      const priceComment = estimatedPrice ? ` | Precio estimado: $${estimatedPrice} USD` : '';
      const payload = {
        type: 'transfer',
        full_name: form.full_name,
        email: form.email,
        whatsapp: form.whatsapp,
        trip_type: tripType,
        pickup: form.pickup,
        destination: form.destination,
        excursion_id: '',
        excursion_title: '',
        date: form.date,
        time: form.time,
        passengers: parseInt(form.passengers || '2', 10),
        vehicle: vehicle.name,
        comments: `${form.comments}${priceComment}`,
      };
      await api.post('/reservations', payload);
      toast.success(lang === 'es' ? '¡Reserva enviada! Te contactaremos por WhatsApp.' : 'Booking sent! We will contact you via WhatsApp.');
      const num = (settings && settings.whatsapp_number) || '18099638198';
      const priceMsg = estimatedPrice ? ` Precio estimado: $${estimatedPrice} USD.` : '';
      const msg = lang === 'es'
        ? `Hola TTS Santos, quiero reservar un traslado de *${form.pickup}* a *${form.destination}*. Vehículo: *${vName}*. Nombre: ${form.full_name}, Fecha: ${form.date} ${form.time || ''}, Pasajeros: ${form.passengers}, Tipo: ${tripType === 'round-trip' ? 'Ida y Vuelta' : 'Solo Ida'}.${priceMsg}`
        : `Hello TTS Santos, I want to book a transfer from *${form.pickup}* to *${form.destination}*. Vehicle: *${vName}*. Name: ${form.full_name}, Date: ${form.date} ${form.time || ''}, Passengers: ${form.passengers}, Type: ${tripType === 'round-trip' ? 'Round Trip' : 'One Way'}.${priceMsg}`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
      onOpenChange(false);
      setForm({ full_name: '', email: '', whatsapp: '', pickup: '', destination: '', date: '', time: '', passengers: '2', comments: '' });
    } catch {
      toast.error(lang === 'es' ? 'Error al enviar' : 'Error sending');
    } finally {
      setLoading(false);
    }
  };

  const paxOptions = [1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30].filter(p => p <= (vehicle.pricing_key === 'shared' ? 15 : vehicle.pricing_key === 'group' ? 30 : 8));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Car className="w-5 h-5 text-[#ff6b1a]" />
            {lang === 'es' ? 'Reservar' : 'Book'} · {vName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 my-3 p-3 bg-black/40 rounded-lg border border-white/5">
          <img src={vehicle.image} alt={vName} className="w-20 h-16 object-cover rounded" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">{vName}</div>
            <div className="text-xs text-white/50 flex items-center gap-1.5"><Users className="w-3 h-3 text-[#ff6b1a]" /> {vehicle.capacity}</div>
          </div>
          {estimatedPrice ? (
            <div className="text-right">
              <div className="text-2xl font-black text-[#ff6b1a]">${estimatedPrice.toFixed(0)}</div>
              <div className="text-[10px] text-white/50 uppercase">{tripType === 'round-trip' ? (lang === 'es' ? 'Ida y vuelta' : 'Round trip') : (lang === 'es' ? 'Solo ida' : 'One way')}</div>
            </div>
          ) : quoteLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#ff6b1a]" />
          ) : null}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Trip type */}
          <div>
            <Label className="text-white/80 mb-2 block text-xs">{lang === 'es' ? 'Tipo de Viaje' : 'Trip Type'}</Label>
            <RadioGroup value={tripType} onValueChange={setTripType} className="flex gap-5">
              <div className="flex items-center gap-2"><RadioGroupItem value="one-way" id="vbow" className="border-white/40 text-[#ff6b1a]" /><Label htmlFor="vbow" className="text-white/80 cursor-pointer">{lang === 'es' ? 'Solo Ida' : 'One Way'}</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="round-trip" id="vbrt" className="border-white/40 text-[#ff6b1a]" /><Label htmlFor="vbrt" className="text-white/80 cursor-pointer">{lang === 'es' ? 'Ida y Vuelta' : 'Round Trip'}</Label></div>
            </RadioGroup>
          </div>

          {/* Pickup / Destination */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs flex items-center gap-1"><MapPin className="w-3 h-3 text-[#ff6b1a]" /> {lang === 'es' ? 'Recogida' : 'Pickup'} *</Label>
              <SearchableLocation value={form.pickup} onChange={(v) => update('pickup', v)} airports={airports} hotels={hotels} placeholder={lang === 'es' ? 'Buscar aeropuerto u hotel...' : 'Search airport or hotel...'} />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs flex items-center gap-1"><MapPin className="w-3 h-3 text-[#ff6b1a]" /> {lang === 'es' ? 'Destino' : 'Destination'} *</Label>
              <SearchableLocation value={form.destination} onChange={(v) => update('destination', v)} airports={airports} hotels={hotels} placeholder={lang === 'es' ? 'Buscar destino...' : 'Search destination...'} />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Fecha' : 'Date'} *</Label>
              <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Hora' : 'Time'}</Label>
              <Input type="time" value={form.time} onChange={(e) => update('time', e.target.value)} className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Pasajeros' : 'Passengers'} *</Label>
              <Select value={form.passengers} onValueChange={(v) => update('passengers', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10">
                  {paxOptions.map(p => <SelectItem key={p} value={String(p)} className="text-white">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Nombre Completo' : 'Full Name'} *</Label>
              <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">WhatsApp *</Label>
              <Input value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} required placeholder="+1 829..." className="bg-black/40 border-white/10 text-white h-10" />
            </div>
          </div>

          <div>
            <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Comentarios (Opcional)' : 'Comments (Optional)'}</Label>
            <Textarea value={form.comments} onChange={(e) => update('comments', e.target.value)} rows={2} className="bg-black/40 border-white/10 text-white" />
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 text-white hover:bg-white/5 flex-1">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading} className="btn-orange border-0 flex-[2] gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Send className="w-4 h-4" /> {lang === 'es' ? `Confirmar Reserva${estimatedPrice ? ` — $${estimatedPrice.toFixed(0)}` : ''}` : `Confirm Booking${estimatedPrice ? ` — $${estimatedPrice.toFixed(0)}` : ''}`}</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleBookingDialog;
